import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  AnswerState,
  AnswerValue,
  Material,
  Operation,
  QTables,
  Question,
  Scratchpad,
  SelectedRowState,
  Tab,
  TableState,
} from "../types";
import { runOperations, spentWhenMarkedDone, rearmBlankRunOnceOps } from "../util/operationRunner";
import { extractMappingSpec, decodeMapping, type MappingSpec } from "../util/mappingDecoder";
import { buildConfigSnapshot, parseConfigSnapshot } from "../util/configSnapshot";
import { getLaunchContext } from "../util/launchContext";
import { currentFieldwise, currentPoUse, describeFieldwise } from "../util/fieldwiseRun";
import { fetchItemCustomerPartNumber, findItemAndPlanName, type ItemMatch, type PlannedName } from "../util/netsuiteClient";
import { getProfile } from "../products";
import { normalizeHeatDutySuffix, suffixNormalizedFields } from "../util/numericSuffix";
import { answerChanged, noteEvent } from "../util/sessionTrack";
import { isBlankAnswer, PREDICTION_DEFAULT_ON, sameAnswer, scoredKeys } from "../util/prediction";
import { originKeepOf } from "../util/boxOrigin";

// The currently-selected ORDER_TYPE answer code (e.g. "NEWBUILD", "BYOB") in an
// answer state, or "" if none. Used to seed the order-type edge-guard on load.
function orderTypeCodeOf(a: AnswerState): string {
  const key = Object.keys(a).find(
    (k) => k.startsWith("ORDER_TYPE/") && a[k] === true
  );
  return key ? key.slice("ORDER_TYPE/".length) : "";
}

/** Drop a table's row selection when no SELECT_HOUSING_<n> answer is true.
 *
 *  The radio renders from selectedRows but the SELECTION is written as answers, by selectRow
 *  and by the CPQ scripts alike. Keeping both without reconciling them means a script can
 *  clear the selection and the radio still shows it -- which is exactly what "Generate
 *  Housing Options" looked like: RESET_SELECT_HOUSING_ON_CALC ran, the answers went false,
 *  and the dot stayed on the row.
 *
 *  Only ever CLEARS, never selects. A table whose answers say row 3 is selected keeps
 *  whatever selectedRows already holds: selectRow sets both together, and inferring an index
 *  back from the answers here would fight it (and the SELECT_HOUSING_<n> slots only go to 5,
 *  so a 6-row table cannot be described by them at all).
 *
 *  Tables with no SELECT_HOUSING answers of any kind are left completely alone -- absence of
 *  the column is not a statement that nothing is selected. */
function reconcileSelectedRows(
  selected: SelectedRowState,
  answers: AnswerState
): SelectedRowState {
  let changed = false;
  const out: SelectedRowState = { ...selected };
  for (const tableCode of Object.keys(out)) {
    const prefix = `${tableCode}/SELECT_HOUSING_`;
    let sawColumn = false;
    let anyTrue = false;
    for (const k of Object.keys(answers)) {
      if (!k.startsWith(prefix)) continue;
      sawColumn = true;
      if (answers[k] === true) {
        anyTrue = true;
        break;
      }
    }
    if (sawColumn && !anyTrue && out[tableCode] !== -1) {
      out[tableCode] = -1;
      changed = true;
    }
  }
  return changed ? out : selected;
}

// Does this answer state have a NECK? Seeds the edge-guard for op 15.5
// (NECK_CHANGE_CLEARS_FLANGE_OD), which blanks the Flange O.D. when a neck appears or
// disappears.
//
// Kevin, 2026-08-27: "my flange od is blanking out and it should not be ... OT-SB26/12-12-1
// when i edit config flange od is no longer there." Loading a configuration WITH a neck onto
// a fresh model that had none is not the user changing anything, but it looks identical to
// the op — so it cleared the very value the item was just restored with.
//
// Must mirror the op's own test exactly: blank counts as NO neck, and so does NECK_NONE.
function hasNeckOf(a: AnswerState): boolean {
  const key = Object.keys(a).find(
    (k) => k.startsWith("NECK_HEIGHT/") && a[k] === true
  );
  const code = key ? key.slice("NECK_HEIGHT/".length) : "";
  return code !== "" && code !== "NECK_NONE";
}

/** An item's configuration as it was stored, for "what did this edit change". */
export type EditBaseline = { itemId: string; itemName: string | null; answers: AnswerState };

// ── PREDICTION (grey pre-fill) ──────────────────────────────────────────────────────────
// A new configuration opens with a past configuration applied whole (api/predict-config)
// and every answer it set shown in a lighter colour until the user changes it.
//
// "Predicted" is decided by VALUE, not by tracking clicks: an answer is grey while it still
// equals what the prediction put there. Any change -- by the user, or by an operation reacting
// to the user -- makes it an ordinary answer. That one rule also drives switching predictions
// off (only still-predicted answers go back), the silent page-2 refresh (only untouched answers
// are replaced) and the outcome (anything that differs at Save & Submit counts as changed).

/** Where a prediction came from: a cpq2 configuration (cpq2_saved_configurations row), a
 *  legacy order's Configuration tab translated into cpq2 answers (api/shared/predict/legacy.js),
 *  or the other burner of the same MB, mirrored (api/shared/predict/mirror.js). */
export type PredictionSource = {
  id: string;
  name: string;
  customerName: string | null;
  createdAt: string;
  basis: "customer" | "everyone";
  /** fieldwise: predicted answer by answer from many configurations (src/util/fieldwise.ts). */
  kind: "cpq2" | "legacy" | "mirror" | "fieldwise";
};

/** A prediction to apply: a whole cpq2 snapshot (applied verbatim, like a Favorite; with
 *  `answers` in place of its own for a mirrored MB burner), or the answers a legacy
 *  configuration translates to (applied like an Item Template load -- overlaid, then the
 *  operations complete it). */
export type PredictionInput = { configSnapshot: string; answers?: AnswerState } | { answers: AnswerState; verbatim?: boolean };

/** An MB burner predicted as the mirror of its sibling: which answers the mirror swapped (the
 *  user should check those) and which it cannot know (the user must fill those in). */
export type MirrorPlan = {
  role: "opposite" | "middle";
  /** Answer keys whose value differs from the sibling's. */
  changed: string[];
  /** Answer keys (table cells) or question codes left blank for the user. */
  needsInput: string[];
};

type PredictionState = {
  on: boolean;
  /** The value the prediction gave each answer key (null = cleared). */
  predicted: AnswerState;
  /** What those keys held before the prediction -- where switching it off returns them. */
  base: AnswerState;
  baseSelected: SelectedRowState;
  baseTables: TableState;
  baseExtraAux: number;
  predSelected: SelectedRowState;
  predTables: TableState;
  predExtraAux: number;
  launchSource: PredictionSource;
  refineSource: PredictionSource | null;
  /** The mirror, while it holds: each swapped key's mirrored value, and what the user must
   *  fill in. Dropped by the first refresh (the burner turned out to differ). */
  mirror: { role: MirrorPlan["role"]; values: AnswerState; needsInput: string[] } | null;
  /** The rep picked this configuration (the Recent menu): nothing re-predicts over it. */
  held: boolean;
  /** Field by field: where each predicted answer came from and how sure (util/fieldwiseRun). */
  provenance: Record<string, { title: string; unsure: boolean }>;
  /** The form as it was when the switch was turned on, and the keys switching on changed:
   *  switching off puts these back (setPredictionOn). Absent while it has never been switched. */
  restore?: { answers: AnswerState; selected: SelectedRowState; tables: TableState; extraAux: number; changed: string[] } | null;
};

const sameSelection = (a: SelectedRowState, b: SelectedRowState) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if ((a[k] ?? -1) !== (b[k] ?? -1)) return false;
  return true;
};

type AnswersCtx = {
  answers: AnswerState;
  tables: TableState;
  selectedRows: SelectedRowState;
  /** `byUser` false: a write the form makes on its own (a rule-locked cell forced to "None"),
   *  which must not count as the user's edit -- the prediction may still fill that cell later. */
  setAnswer: (qCode: string, aCode: string, val: AnswerValue, byUser?: boolean) => void;
  selectRow: (tableCode: string, rowIdx: number) => void;
  runOps: (overlay?: AnswerState) => {
    ran: Array<{ id: string; name: string; ok: boolean; error?: string }>;
  };
  // Question codes flagged as unanswered by the Save & Submit validation.
  // Widgets read this to tint their container red; cleared as each is answered.
  invalidFields: ReadonlySet<string>;
  setInvalidFields: (codes: string[]) => void;
  // Optional Customer Part Number to assign on Save & Submit. Kept OUT of
  // `answers` so it never feeds the CPQ operations / generated ID key.
  customerPartNumber: string;
  setCustomerPartNumber: (val: string) => void;
  // Replace the CPN field with a specific item's real Customer Part Number,
  // clearing it when that item has none. See the implementation for why this
  // overwrites while the launch-time seed does not.
  syncCustomerPartNumberForItem: (itemId: string | null) => Promise<void>;
  // Set a KNOWN Customer Part Number (or clear with null). For sources that
  // already carry it and need no lookup — e.g. loading a saved Favorite.
  applyCustomerPartNumber: (value: string | null) => void;
  // Extra inlet-connection Aux columns the user added beyond the built-in
  // Aux 1-3 (their values overflow to custiteminlet_connection_notes on submit).
  extraAuxColumns: number;
  setExtraAuxColumns: (n: number) => void;
  // Load an existing item's custitem_mapping into the configurator (the Item
  // Template shortcut): decode the string and apply the answers, then re-run.
  loadItemTemplate: (mapping: string) => { selects: number; values: number };
  // Restore a full configuration snapshot (custitem_cpq2configuration). Async:
  // the df1 format inflates via DecompressionStream.
  // `source`: the item this snapshot was read from. Recorded as the edit baseline, so Save &
  // Submit can tell its creator what changed (util/configDiff). Omitted for a Favorite.
  loadItemConfig: (
    configJson: string,
    fieldOverrides?: AnswerState,
    source?: { itemId: string; itemName?: string | null }
  ) => Promise<{ count: number }>;
  /** The configuration as its item last stored it, or null when this session did not load
   *  one. Save & Submit diffs against it, then moves it forward to what it saved. */
  getEditBaseline: () => EditBaseline | null;
  setEditBaseline: (b: EditBaseline | null) => void;
  // Serialize the current configurator state for storing on the item. Async:
  // the df1 format deflates via CompressionStream.
  getConfigSnapshot: () => Promise<string>;
  // Edit-vs-New config mode. `editItemId` is the internal id of the item Save &
  // Submit updates IN PLACE (Edit Config); null means "make a new part" (New
  // Config). `configMode` is the derived label the status badge shows. It starts
  // 'edit' on an Edit Config launch (a line + item id on the URL), and flips to
  // 'new' when the launched item is locked (custitem_cannoteditcpq) or the user
  // loads a different configuration over it (Item Template / Favorites).
  editItemId: string | null;
  configMode: "edit" | "new";
  /** Whether Save & Submit will edit, reuse an existing item, or create a new part.
   *  "existing" wins over the launched mode. Only "new" earns scoreboard points. */
  configStatus: "edit" | "existing" | "new";
  /** The NetSuite item this configuration already matches, or null. One shared
   *  lookup - see the effect that sets it. */
  existingItem: ItemMatch | null;
  /** The fingerprint `existingItem` was looked up for, or "" when no lookup has completed.
   *  Save & Submit reuses the match only when this equals the current fingerprint. */
  existingItemFor: string;
  /** The part number this configuration would get, pre-resolved alongside `existingItem`.
   *  Advisory: Save & Submit passes it and the server re-verifies it. */
  plannedName: PlannedName | null;
  // Force New Config: clears editItemId so Save & Submit creates a new part and
  // the write-back adds a new line (rather than editing the launched one).
  forceNewConfig: () => void;
  // Enter Edit config on a specific item: Save & Submit UPDATES that item in
  // place (its BOM/routing get rebuilt too). Used when an Item Template is loaded
  // for an item that is NOT locked (custitem_cannoteditcpq false).
  editExistingItem: (itemId: string) => void;
  /** ADMIN OVERRIDE for custitem_cannoteditcpq.
   *
   *  `lockedItemId` is the launched item that was refused as locked -- remembered so the
   *  override has something to re-enter Edit mode ON. `adminOverride` is the code the
   *  operator typed, held in memory only: not localStorage, not sessionStorage, so it dies
   *  with the tab and cannot be picked up by the next person on a shared machine.
   *
   *  The code is NOT a permission. It is re-validated server-side on the request that
   *  writes -- see api/shared/admin-override.js -- so a UI that has been unlocked is only a
   *  UI. */
  lockedItemId: string | null;
  noteLockedItem: (itemId: string) => void;
  adminOverride: string | null;
  setAdminOverride: (code: string | null) => void;
  // The encoder's mapping spec (slots + TOKEN_MAPS), extracted from the MAPPING
  // operation so it never drifts. Widgets use it to validate a typed value
  // against the finite set a slot can encode (e.g. the Housing Height column).
  mappingSpec: MappingSpec | null;
  // Which configurator is running (a products.json slug). Exposed because a
  // question CODE is only unique within a product, so anything keyed on codes has
  // to know which CPQ it is looking at -- see isManuallyHidden.
  product: string;
  /** The grey pre-fill, if one was applied: whether it is showing and where it came from. */
  prediction: {
    on: boolean;
    launchSource: PredictionSource;
    refineSource: PredictionSource | null;
    /** Set while an MB mirror is the prediction. */
    mirrorRole: MirrorPlan["role"] | null;
    /** The rep picked it from the Recent menu: no automatic refresh. */
    held: boolean;
  } | null;
  /** Is this answer key still showing its predicted value (render it grey)? */
  isPredicted: (key: string) => boolean;
  /** For a predicted answer still showing: where it came from and whether to double-check it. */
  predictionNote: (key: string) => { title: string; unsure: boolean } | null;
  /** Is it still showing a value the MB mirror swapped from the other burner (mark it)? */
  isMirrored: (key: string) => boolean;
  /** Is it a table cell the MB mirror left blank for the user, still blank? */
  needsMirrorInput: (key: string) => boolean;
  /** The table cells the MB mirror left for the user (the middle burner's In / Up), filled or
   *  not: required fields while the mirror stands (util/validation requiredCells). */
  mirrorRequiredCells: string[];
  /** Did the prediction give this key a value (whatever it holds now)? */
  wasPredicted: (key: string) => boolean;
  /** Apply a predicted configuration to a fresh New Config. Resolves false if not applied. */
  applyPrediction: (
    input: PredictionInput,
    source: PredictionSource,
    mirror?: MirrorPlan | null,
    /** replace: start from the form as it was before any prediction (a Recent pick replaces the
     *  prediction whole); held: nothing re-predicts over it; only: apply just these answer keys
     *  and let the operations complete the rest (a launch fills the first page only --
     *  util/prediction launchKeys); show: write it into the form even when PREDICTION_DEFAULT_ON
     *  is off (the rep asked for this configuration). */
    opts?: { replace?: boolean; held?: boolean; only?: (key: string) => boolean; provenance?: Record<string, { title: string; unsure: boolean }>; show?: boolean }
  ) => Promise<boolean>;
  /** Replace still-untouched answers with a better-matching configuration. `leaveAlone` names
   *  keys not to touch even if untouched (the first page, for the page-2 refresh). Answers the
   *  user changed are never replaced. */
  refinePrediction: (
    input: PredictionInput,
    source: PredictionSource,
    leaveAlone: (key: string) => boolean,
    /** userEdits: "untouched" means the user never edited it, rather than "still the launch
     *  value" -- for the pages the launch did not fill, where the operations have since set
     *  defaults from the housing the user chose. */
    opts?: { userEdits?: boolean; provenance?: Record<string, { title: string; unsure: boolean }> }
  ) => Promise<boolean>;
  /** Show or remove the prediction. Off puts every still-predicted answer back to what it
   *  was before; answers the user changed are kept either way. */
  setPredictionOn: (on: boolean) => void;
  /** The form as it stands now, kept: answers set on it with every operation run, on a scratch
   *  copy (nothing on screen changes). Completes a past legacy order into what the form would
   *  show for it (src/util/fieldwiseRun.ts). Take it before any prediction is applied. */
  formCompleter: () => (answers: AnswerState) => AnswerState;
  /** At Save & Submit: how many visible answers the prediction filled and which changed. */
  predictionOutcome: (isScored: (key: string) => boolean) => { predicted: number; changed: string[]; detail: Array<{ key: string; predicted: string; final: string; from?: string }>; bySource: Record<string, number> } | null;
};

const Ctx = createContext<AnswersCtx | null>(null);

type ProviderProps = {
  // Which configurator is running (a products.json slug). Stamped into every
  // snapshot this session writes, so Edit Config can reopen the right CPQ later.
  product: string;
  questions: Question[];
  qtables: QTables;
  operations: Operation[];
  materials: Material[];
  tabs: Tab[];
  children: ReactNode;
};

export function AnswersProvider({
  product,
  questions,
  qtables,
  operations,
  materials,
  tabs,
  children,
}: ProviderProps) {
  // Seed a few free-text answers with sensible defaults so reactive scripts
  // (notably `density`) produce real numbers on the first runOps. Without
  // ELEVATION/TEMPERATURE seeded, density reads undefined → AIR_PRESSURE = NaN.
  const [answers, setAnswers] = useState<AnswerState>(() => ({
    "ELEVATION/ELEVATION": "0",
    "TEMPERATURE/TEMPERATURE": "60",
  }));
  // Scratchpad persists across operation runs (prevHousing-style state).
  const scratchpadRef = useRef<Scratchpad>({});
  // Latest runOps + a debounce handle so a plain answer edit re-runs the
  // operation pipeline. NetSuite CPQ re-evaluates reactive scripts on every
  // answer change; without this, dependent defaults, validations, mappings and
  // the generated ID key only refresh on Calculate / row-select / mount.
  const runOpsRef = useRef<(() => void) | null>(null);
  const reactiveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pre-seed tables with the real QTables shipped in qtable_data.json (DESIGN,
  // DIMENSION_HEIGHT, INVENTORY_ITEMS, ORIFICE_CAPACITY_CHART). Type=11
  // questions that scripts treat as tables (BURNER_TYPE) get populated at
  // runtime by their generating operations.
  const [tables, setTables] = useState<TableState>(() => {
    const initial: TableState = {};
    if (!qtables) return initial;
    for (const code of Object.keys(qtables)) {
      const qt = qtables[code];
      if (!qt) continue;
      const rows = Array.isArray(qt.rows) ? qt.rows : [];
      initial[code] = rows.map((r) =>
        r && typeof r === "object" ? { ...r } : {}
      );
    }
    return initial;
  });
  const [selectedRows, setSelectedRows] = useState<SelectedRowState>({});
  const [invalidFields, setInvalidFieldsState] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const [customerPartNumber, setCustomerPartNumber] = useState<string>("");
  // Edit-vs-New config mode. Seed from the launch context: the "Edit Config"
  // button passes BOTH a line and the line's item id, so an item id present at
  // launch means "edit that item". "Configure" passes neither -> new part.
  // forceNewConfig() (below) clears this when the item is locked or the user
  // loads a different configuration.
  const [editItemId, setEditItemId] = useState<string | null>(() => {
    const ctx = getLaunchContext();
    return ctx.itemId ? String(ctx.itemId) : null;
  });
  const forceNewConfig = useCallback(() => {
    setEditItemId(null);
    // A new part follows the Box-Type origin rule whatever the loaded item said (util/boxOrigin).
    delete scratchpadRef.current.originKeep;
  }, []);
  const [lockedItemId, setLockedItemId] = useState<string | null>(null);
  const [adminOverride, setAdminOverride] = useState<string | null>(null);
  const noteLockedItem = useCallback((id: string) => setLockedItemId(id ? String(id) : null), []);
  const editExistingItem = useCallback(
    (id: string) => setEditItemId(id ? String(id) : null),
    []
  );
  // A ref, not state: nothing renders from it, and Save & Submit reads it once per submit.
  const editBaselineRef = useRef<EditBaseline | null>(null);
  const getEditBaseline = useCallback(() => editBaselineRef.current, []);
  const setEditBaseline = useCallback((b: EditBaseline | null) => {
    editBaselineRef.current = b;
  }, []);

  // Pre-fill the Customer Part Number field ("populate the CPN in the cpq").
  // Priority: the launched sales-order LINE's CPN (buttons.js passes ?cpn=), else
  // the launched / loaded ITEM's stored CPN for this customer (API lookup). Seeds
  // at most once and never overwrites a value the user typed (the functional
  // `prev || …` guard), so a manual edit always wins. Favorites / locked Item
  // Template loads carry no editItemId, so they fall through to no pre-fill.
  const cpnPrefillDone = useRef(false);
  useEffect(() => {
    if (cpnPrefillDone.current) return;
    const ctx = getLaunchContext();
    const lineCpn = (ctx.customerPartNumber ?? "").trim();
    if (lineCpn) {
      cpnPrefillDone.current = true;
      setCustomerPartNumber((prev) => prev || lineCpn);
      return;
    }
    // Wait until an item's internal id is known. customerId is NOT required:
    // fetchItemCustomerPartNumber omits it and the API then falls back to "any
    // CPN for this item", so a launch without ?customerid= still sources one.
    // Requiring it used to skip sourcing entirely on those launches.
    if (!editItemId) return;
    cpnPrefillDone.current = true;
    let alive = true;
    fetchItemCustomerPartNumber(editItemId, ctx.customerId).then((cpn) => {
      if (alive && cpn) setCustomerPartNumber((prev) => prev || cpn);
    });
    return () => {
      alive = false;
    };
  }, [editItemId]);

  /** Point the Customer Part Number field at a specific item: load that item's
   *  real CPN, or CLEAR the field when it has none.
   *
   *  Used by the Item Template "Load" button. Loading a different item must not
   *  leave the previous item's part number sitting in the field — that number
   *  belongs to another part, and on save it would be claimed away from it.
   *
   *  Unlike the launch-time seed above this OVERWRITES, because it is an explicit
   *  user action rather than a background guess; it also marks the seed as done so
   *  that effect can't re-apply a stale value afterwards (loading an item changes
   *  editItemId, which is the seed effect's dependency). Best-effort: a lookup
   *  failure leaves the field cleared rather than showing the wrong number. */
  const syncCustomerPartNumberForItem = useCallback(async (itemId: string | null) => {
    cpnPrefillDone.current = true;
    if (!itemId) {
      setCustomerPartNumber("");
      return;
    }
    const cpn = await fetchItemCustomerPartNumber(itemId, getLaunchContext().customerId);
    setCustomerPartNumber(cpn ?? "");
  }, []);

  /** Put a KNOWN Customer Part Number into the field (or clear it with null/"").
   *
   *  For sources that already carry the number and need no lookup — today, loading
   *  a saved Favorite, whose record stores `customer_part_number`. Without this the
   *  Favorites load left the PREVIOUS configuration's number sitting in the field,
   *  and on save that number would have been claimed away from the part it actually
   *  belongs to (renamed to `<number>_2`) — the same hazard the Item Template load
   *  already guards against.
   *
   *  Overwrites, and marks the launch-time seed as done so it cannot re-apply a
   *  stale value afterwards. */
  const applyCustomerPartNumber = useCallback((value: string | null) => {
    cpnPrefillDone.current = true;
    setCustomerPartNumber((value ?? "").trim());
  }, []);

  // ── DOES THIS CONFIGURATION ALREADY EXIST IN NETSUITE? ────────────────────
  // A debounced lookup on the config's fingerprint, lifted to the context because
  // THREE things depend on the answer and they must not be able to disagree:
  //   ConfigModeBadge  shows "Existing Config"
  //   ProgressBar      decides whether to promise points
  //   SaveSubmitButton brings the matched item in and builds nothing
  // It used to live inside ConfigModeBadge, so anything else needing it would have run a
  // SECOND lookup and could have reached a different conclusion. One lookup, one answer.
  //
  // Which answer holds the fingerprint is per configurator (Natural Draft asks
  // HIDDEN_ID_KEY/ID_KEY, Flame Cells ID_KEY/ID), and the key only changes when the
  // configuration materially changes, so this is naturally throttled on top of the debounce.
  const fingerprintKey = getProfile(product).fingerprintAnswerKey;
  const fingerprint = fingerprintKey ? String(answers[fingerprintKey] ?? "").trim() : "";
  const [existingItem, setExistingItem] = useState<ItemMatch | null>(null);
  // WHICH fingerprint the value above is an answer to.
  //
  // Save & Submit needs the same lookup and was paying for it a second time -- 1649ms of a
  // measured 13.5s wait, re-asking a question this effect had already answered while the
  // user was still reading the form. It can only reuse the answer if it knows the answer is
  // about the configuration in front of it: a cached match from a PREVIOUS fingerprint would
  // either reuse the wrong item or skip creating one. Empty until a lookup completes, so an
  // in-flight or never-run lookup never matches and the caller does its own.
  const [existingItemFor, setExistingItemFor] = useState<string>("");
  // The part number this configuration would be given, resolved in the SAME background call
  // rather than during the user's wait. Advisory only -- create-assembly re-verifies it.
  const [plannedName, setPlannedName] = useState<PlannedName | null>(null);
  useEffect(() => {
    if (!fingerprint) {
      setExistingItem(null);
      setExistingItemFor("");
      setPlannedName(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      findItemAndPlanName(fingerprint).then((res) => {
        if (cancelled) return;
        setExistingItem(res.item);
        setPlannedName(res.plannedName);
        // Set TOGETHER with the results and only on completion, so the three are always
        // consistent -- the fingerprint is a claim that they were fetched for it.
        setExistingItemFor(fingerprint);
      });
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [fingerprint]);

  /**
   * What Save & Submit will actually do, and therefore whether any credit is due.
   *
   * "existing" WINS over the launched mode, exactly as the badge has always shown it: a
   * configuration matching a NetSuite item builds nothing regardless of how cpq2 was
   * opened. Only "new" genuinely creates a part, so only "new" earns points.
   */
  const configStatus: "edit" | "existing" | "new" = existingItem
    ? "existing"
    : editItemId
      ? "edit"
      : "new";

  // Encoder mapping spec (cached inside extractMappingSpec); static per operations.
  const mappingSpec = useMemo(() => extractMappingSpec(operations), [operations]);
  const [extraAuxColumns, setExtraAuxColumnsState] = useState<number>(0);
  // Clamp 0..12 extra Aux columns (Aux 4 .. Aux 15).
  const setExtraAuxColumns = useCallback((n: number) => {
    setExtraAuxColumnsState(Math.max(0, Math.min(12, Math.floor(n) || 0)));
  }, []);

  // The latest state for the prediction callbacks, which run after awaits and from other
  // components' effects, where a closed-over copy could be a render behind.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const selectedRowsRef = useRef(selectedRows);
  selectedRowsRef.current = selectedRows;
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const extraAuxRef = useRef(extraAuxColumns);
  extraAuxRef.current = extraAuxColumns;

  const [prediction, setPredictionState] = useState<PredictionState | null>(null);
  const predictionRef = useRef<PredictionState | null>(null);
  const setPrediction = useCallback((p: PredictionState | null) => {
    predictionRef.current = p;
    setPredictionState(p);
  }, []);

  const setInvalidFields = useCallback((codes: string[]) => {
    setInvalidFieldsState(new Set(codes));
  }, []);

  // Every answer key the USER has set (setAnswer, a housing pick). The page-2 prediction
  // replaces only answers nobody edited -- by key, so one cell an edit or a lock wrote does not
  // shield the rest of its table.
  const userEditedRef = useRef<Set<string>>(new Set());
  // What the user changed since the prediction was last switched on: switching off keeps it.
  const editedSinceOnRef = useRef<Set<string>>(new Set());

  const setAnswer = useCallback(
    (qCode: string, aCode: string, val: AnswerValue, byUser = true) => {
      // A lock forcing "None" into a cell is the form's doing, not the user's: at launch every
      // inlet is unsized, so every position cell is locked and written, and counting those as
      // edits left Inlet Housing Position blank after the page-2 prediction (2026-10-19).
      if (byUser) {
        userEditedRef.current.add(`${qCode}/${aCode}`);
        editedSinceOnRef.current.add(`${qCode}/${aCode}`);
      }
      answerChanged(qCode);
      setAnswers((prev) => ({ ...prev, [`${qCode}/${aCode}`]: val }));
      // Once the user touches a flagged field, clear its red highlight.
      setInvalidFieldsState((prev) => {
        if (!prev.has(qCode)) return prev;
        const next = new Set(prev);
        next.delete(qCode);
        return next;
      });
      // Debounced reactive re-run: coalesce a burst of edits (e.g. a select
      // writing true then null across its options) into one pipeline pass.
      // Run-once ops (those gated on SUBMITTED, e.g. Clear Qtable / startup
      // defaults) stay protected by operationRunner's ranOnce guard, so this
      // does not re-wipe the hand-configured replacement housing row.
      if (reactiveTimer.current) clearTimeout(reactiveTimer.current);
      reactiveTimer.current = setTimeout(() => {
        reactiveTimer.current = null;
        runOpsRef.current?.();
      }, 150);
    },
    []
  );

  const runOps = useCallback(
    (
      overlay: AnswerState = {},
      seedAnswers?: AnswerState,
      seedSelected?: SelectedRowState
    ) => {
      const result = runOperations(
        operations,
        {
          answers: seedAnswers ?? answers,
          tables,
          selectedRows: seedSelected ?? selectedRows,
          questions,
          qtables,
          tabs,
          materials,
          scratchpad: scratchpadRef.current,
          // So mapping mode uses THIS configurator's hide-exempt fields.
          product,
        },
        overlay
      );
      scratchpadRef.current = result.state.scratchpad;
      if (import.meta.env?.DEV) {
        console.log(
          "[CPQ] runOps complete. tables:",
          result.state.tables,
          "ran:",
          result.ran
        );
      }
      setAnswers(result.state.answers);
      setTables(result.state.tables);
      // THE ANSWERS DECIDE WHAT THE RADIO SHOWS.
      //
      // Kevin, 2026-08-27: "if i click generate housing options then the select housing
      // radio should reset if it is set." The operation that does that already exists --
      // RESET_SELECT_HOUSING_ON_CALC, seq 11, gated on CALCULATE2 -- and it was running.
      // It just could not be SEEN: it clears the SELECT_HOUSING_<n> ANSWERS, while the radio
      // renders from selectedRows (TableWidget passes selectedIdx={selectedRows[qCode]}).
      // Two parallel stores for one fact, and an operation can only reach one of them.
      //
      // So selectedRows is reconciled against the answers on every run: a table with no
      // SELECT_HOUSING_<n> true has nothing selected. That makes the answers the single
      // source of truth and means ANY script that clears them clears the radio, rather than
      // this one op needing a bespoke hook the next one would also need.
      setSelectedRows(reconcileSelectedRows(result.state.selectedRows, result.state.answers));
      return { ran: result.ran };
    },
    [operations, questions, qtables, tabs, materials, answers, tables, selectedRows]
  );

  // Mark every run-once startup/clear op as already-done. Called when a config is
  // LOADED (Edit Config / Item Template) so those ops can't fire on the first
  // subsequent edit and clobber the just-loaded values. Notably CLEAR_QTABLE is
  // gated on (BYOB|replacement) && !SUBMITTED, so it does NOT run at mount (the
  // default NEW-BUILD order doesn't satisfy its rule) — without this it fires on
  // the first edit after a BYOB config loads and blanks the "Housing Options" row.
  const markRunOnceOpsComplete = useCallback(() => {
    const sp = scratchpadRef.current as Record<string, unknown>;
    const ran = (sp._ranOnce instanceof Set ? sp._ranOnce : (sp._ranOnce = new Set<string>())) as Set<string>;
    for (const op of operations) {
      if (spentWhenMarkedDone(op)) ran.add(op.id);
    }
  }, [operations]);

  // Point the reactive-run ref at the latest runOps, which closes over the
  // freshest answers/tables, so the debounced edit handler runs with current
  // state rather than a stale snapshot.
  useEffect(() => {
    runOpsRef.current = () => runOps();
  }, [runOps]);

  // Cancel any pending reactive run on unmount.
  useEffect(
    () => () => {
      if (reactiveTimer.current) clearTimeout(reactiveTimer.current);
    },
    []
  );

  // Selecting a row in a type=11 table is an event:
  //   1. Mark SELECT_<col>_<n> = true for the chosen row, others null/false.
  //   2. Propagate the row's identifying value into the matching "header"
  //      answer (e.g. BURNER_TYPE → HOUSING/HOUSING).
  //   3. Re-run operations so downstream logic (validation, tab visibility)
  //      sees the new state.
  const selectRow = useCallback(
    (tableCode: string, rowIdx: number) => {
      const rows = tables[tableCode] ?? [];
      const row = rows[rowIdx];

      const nextAnswers: AnswerState = { ...answers };
      // SELECT_HOUSING_<n>: true for selected, false for others, up to 5 slots
      // (matches the "reset selection" scripts' assumption).
      for (let i = 0; i < Math.max(rows.length, 5); i++) {
        const key = `${tableCode}/SELECT_HOUSING_${i + 1}`;
        nextAnswers[key] = i === rowIdx ? true : false;
      }
      // For BURNER_TYPE, also push the row's housing name into HOUSING so the
      // Firetube tab + housing-conditional groups become visible.
      if (tableCode === "BURNER_TYPE" && row) {
        const housing = row["BURNER_NAME"];
        if (housing !== undefined && housing !== null) {
          nextAnswers["HOUSING/HOUSING"] = housing;
        }
      }

      const nextSelected: SelectedRowState = { ...selectedRows, [tableCode]: rowIdx };
      for (const k of Object.keys(nextAnswers)) {
        if (nextAnswers[k] !== answers[k]) {
          userEditedRef.current.add(k);
          editedSinceOnRef.current.add(k);
        }
      }

      // Run ops once with the freshly chosen selection so reactive scripts fire.
      runOps({}, nextAnswers, nextSelected);
    },
    [answers, tables, selectedRows, runOps]
  );

  // Item Template shortcut: decode an existing item's custitem_mapping and
  // overlay its answers onto the current config, then re-run operations so the
  // derived state is rebuilt. Merges over the current answers (DEFAULT_STARTUP
  // has already run once, so it won't clobber the loaded values).
  const loadItemTemplate = useCallback(
    (mapping: string) => {
      const spec = extractMappingSpec(operations);
      if (!spec) throw new Error("Could not read the mapping spec from operations.");
      noteEvent("item-template-load");
      // A loaded template replaces the guess: nothing on screen is predicted any more.
      setPrediction(null);
      const decoded = decodeMapping(mapping, spec, questions);
      const next: AnswerState = { ...answers };
      for (const { question, answerCode } of decoded.selects) {
        const q = questions.find(
          (x) => x.custrecord_cpqc_question_code === question
        );
        if (!q) continue;
        // Set the chosen answer true and clear the question's other options.
        for (const a of q._answers) {
          next[`${question}/${a.custrecord_cpqc_answer_code}`] =
            a.custrecord_cpqc_answer_code === answerCode ? true : null;
        }
      }
      for (const [k, v] of Object.entries(decoded.values)) next[k] = v;
      // A mapping is a partial picture of the item, so it is no baseline to report an edit
      // against. Clearing it means the edit is reported with its before-values unknown.
      editBaselineRef.current = null;
      // The item's box origins stay as saved until a panel changes (util/boxOrigin).
      // A locked item turns into a new part via forceNewConfig, which drops this.
      scratchpadRef.current.originKeep = originKeepOf(next);
      // Same guard as loadItemConfig: don't let run-once clear/startup ops fire
      // during (or after) this load and wipe the overlaid values.
      markRunOnceOpsComplete();
      // The runOps below must not mistake this template load for a manual switch
      // into New Build - Sizing (op RESET_HOUSING_ON_NEWBUILD_SWITCH).
      scratchpadRef.current.prevOrderTypeForHousingReset = orderTypeCodeOf(next);
      // And the neck edge-guard, for the same reason: a template that brings a neck with it
      // is not the user adding one, and op NECK_CHANGE_CLEARS_FLANGE_OD would otherwise
      // clear the Flange O.D. this load just set.
      scratchpadRef.current.prevHasNeckForFlangeOd = hasNeckOf(next);
      runOps({}, next, selectedRows);
      return {
        selects: decoded.selects.length,
        values: Object.keys(decoded.values).length,
      };
    },
    [operations, questions, answers, selectedRows, runOps, markRunOnceOpsComplete, setPrediction]
  );

  // Serialize the full configurator state for storing on the item
  // (custitem_cpq2configuration). Only the dynamically-generated tables are
  // included — the static QTables (DESIGN, ORIFICE_CAPACITY_CHART, …) are
  // re-seeded from qtable_data.json on load, so storing them would just bloat
  // the field.
  const getConfigSnapshot = useCallback(
    (): Promise<string> =>
      buildConfigSnapshot({ answers, selectedRows, tables, extraAux: extraAuxColumns, product }, qtables),
    [answers, selectedRows, tables, qtables, extraAuxColumns, product]
  );

  // Heal values a stored configuration may carry from before an input fix (see the comment in
  // loadItemConfig). Used for anything applied verbatim: a load or a prediction.
  const normalizeLoaded = useCallback(
    (raw: AnswerState): AnswerState => {
      const merged: AnswerState = { ...raw };
      for (const qCode of suffixNormalizedFields(product)) {
        const key = `${qCode}/${qCode}`;
        const v = merged[key];
        if (typeof v === "string" && v !== "") {
          const clean = normalizeHeatDutySuffix(v);
          if (clean !== v) merged[key] = clean;
        }
      }
      return merged;
    },
    [product]
  );

  // Keep the reactive edge-guards in step with a state that was SET rather than edited
  // (a load, a prediction), so the next real edit is not read as a transition.
  const syncEdgeGuards = useCallback((a: AnswerState) => {
    // The prevHousing guards (ops 363/364/394): a later edit must not reset neck height /
    // hinge just because the housing arrived from outside.
    const housing = String(a["HOUSING/HOUSING"] ?? "");
    scratchpadRef.current.prevHousing = housing;
    scratchpadRef.current.neckPrevHousing = housing;
    // The order-type guard (op RESET_HOUSING_ON_NEWBUILD_SWITCH): the first edit must not be
    // mistaken for a manual switch INTO New Build - Sizing, which wipes the housing.
    scratchpadRef.current.prevOrderTypeForHousingReset = orderTypeCodeOf(a);
    // The neck guard (op NECK_CHANGE_CLEARS_FLANGE_OD). Loading an item that HAS a neck onto
    // a model that had none is not the user adding one; without this the op read it as a
    // transition and blanked the Flange O.D. -- Kevin on OT-SB26/12-12-1: "flange od is no
    // longer there".
    scratchpadRef.current.prevHasNeckForFlangeOd = hasNeckOf(a);
  }, []);

  // REFRESH THE DERIVED TEXT. A state applied verbatim is right for ANSWERS -- they are the
  // user's configuration -- but wrong for the values the operations COMPUTE from them.
  // Description, sales description, display name and the id key were all frozen at the moment
  // the configuration was saved, so loading a favourite and submitting it wrote the OLD
  // description onto the item however many times the rules had changed since. That is exactly
  // what was reported: a favourite saved this morning kept printing the internal plugged core
  // length and a "Wrap Inspection" that the rules no longer produce.
  //
  // Only the value-computing operations are re-run (ID_KEY_GENERATOR and DESCRIPTION), NOT the
  // whole pipeline: the reactive ops wipe the Calculate-gated housing table and can re-derive
  // HOUSING away. These two read answers and write derived values, so they cannot disturb the
  // configuration.
  //
  // Best-effort by design: if a derived value cannot be rebuilt, the applied value stays. A
  // configuration that loads with stale text beats one that refuses to load.
  const refreshDerived = useCallback(
    (a: AnswerState, t: TableState, sel: SelectedRowState) => {
      try {
        const derived = operations.filter((o) => {
          const code = String(o.custrecord_cpqc_operation_code ?? "");
          return code === "ID_KEY_GENERATOR" || code === "DESCRIPTION";
        });
        if (!derived.length) return;
        const res = runOperations(derived, {
          answers: { ...a },
          tables: t,
          selectedRows: sel,
          questions,
          qtables,
          tabs,
          materials,
          scratchpad: scratchpadRef.current,
          product,
        } as never) as { state: { answers: AnswerState } };
        if (res && res.state && res.state.answers) setAnswers(res.state.answers);
      } catch (e) {
        console.warn("[cpq2] could not refresh derived values", e);
      }
    },
    [operations, questions, qtables, tabs, materials, product]
  );

  // Apply a whole configuration snapshot VERBATIM (answers, selection, generated tables, Aux
  // columns). Shared by loadItemConfig and the prediction so both restore a configuration the
  // same, already-proven way.
  const applySnapshotState = useCallback(
    (
      merged: AnswerState,
      snap: { selectedRows: SelectedRowState; tables: TableState; extraAux: number }
    ) => {
      // Replace answers + selection; MERGE tables so the static QTables (re-seeded on mount)
      // survive while the snapshot's generated tables (BURNER_TYPE) win.
      setAnswers(merged);
      setSelectedRows(snap.selectedRows);
      const nextTables = { ...tablesRef.current, ...snap.tables };
      setTables(nextTables);
      setExtraAuxColumnsState(snap.extraAux);
      syncEdgeGuards(merged);
      // Don't let run-once startup/clear ops fire on the first edit and wipe the applied
      // values (e.g. CLEAR_QTABLE blanking the BYOB Housing Options row).
      markRunOnceOpsComplete();
      refreshDerived(merged, nextTables, snap.selectedRows);
    },
    [syncEdgeGuards, markRunOnceOpsComplete, refreshDerived]
  );

  // Restore a full configuration snapshot saved on the item. Applies the saved
  // state VERBATIM (no operation re-run): the snapshot is already the post-ops
  // result, and re-running would wipe the Calculate-gated housing table and can
  // re-derive HOUSING away. The reactive edge-guards are synced so the next edit
  // doesn't re-apply a housing default over the loaded values.
  const loadItemConfig = useCallback(
    async (
      configJson: string,
      fieldOverrides?: AnswerState,
      source?: { itemId: string; itemName?: string | null }
    ) => {
      const snap = await parseConfigSnapshot(configJson);
      // An item (Edit Config's restore, or a loaded part) or a Favorite, which has no item.
      noteEvent(source?.itemId ? "config-load:item" : "config-load:favorite");
      // Item-field values (e.g. custiteminlet_connection_notes) fill GAPS the
      // snapshot doesn't already have. The snapshot wins where it has a value, so
      // a value cleared in cpq2 isn't resurrected by a now-stale item field; a
      // legacy item with no snapshot answer still surfaces its field value.
      const merged: AnswerState = { ...snap.answers };
      if (fieldOverrides) {
        for (const [k, v] of Object.entries(fieldOverrides)) {
          if (!(k in merged) && v != null && v !== "") merged[k] = v;
        }
      }
      // Heal a value saved with thousands separators. A comma in Required Heat Duty
      // makes every numeric hide rule that gates the Main Assembly Size options
      // coerce to NaN, so the list comes up EMPTY. The input now strips separators
      // as they are typed, but a configuration SAVED before that fix still carries
      // them, and this path applies the snapshot verbatim with no operation re-run —
      // so normalize on the way in or the bug rides along with the old config.
      for (const qCode of suffixNormalizedFields(product)) {
        const key = `${qCode}/${qCode}`;
        const v = merged[key];
        if (typeof v === "string" && v !== "") {
          const clean = normalizeHeatDutySuffix(v);
          if (clean !== v) merged[key] = clean;
        }
      }
      // What the item holds, before anyone touches it. A Favorite is not an item, so it
      // clears the baseline rather than leaving the previous item's in place.
      editBaselineRef.current = source?.itemId
        ? { itemId: String(source.itemId), itemName: source.itemName ?? null, answers: merged }
        : null;
      // An item keeps its saved box origins until a panel changes (util/boxOrigin); a Favorite
      // is a new part and follows the rule.
      if (source?.itemId) scratchpadRef.current.originKeep = originKeepOf(merged);
      else delete scratchpadRef.current.originKeep;
      // Loading a configuration replaces whatever was predicted; nothing on screen is a
      // guess any more.
      setPrediction(null);
      applySnapshotState(merged, snap);
      return { count: Object.keys(merged).length };
    },
    [applySnapshotState, setPrediction, product]
  );

  // ── PREDICTION ────────────────────────────────────────────────────────────────────────
  // See the PredictionState comment at the top of this file for the rule.

  const isPredicted = useCallback(
    (key: string) => {
      const p = prediction;
      if (!p || !p.on || !(key in p.predicted)) return false;
      const pv = p.predicted[key];
      return !isBlankAnswer(pv) && sameAnswer(answers[key], pv);
    },
    [prediction, answers]
  );

  const wasPredicted = useCallback(
    (key: string) => !!prediction && prediction.on && key in prediction.predicted && !isBlankAnswer(prediction.predicted[key]),
    [prediction]
  );

  const predictionNote = useCallback(
    (key: string) => {
      const p = prediction;
      if (!p || !p.on || !isPredicted(key)) return null;
      return p.provenance[key] ?? null;
    },
    [prediction, isPredicted]
  );

  const isMirrored = useCallback(
    (key: string) => {
      const m = prediction?.on ? prediction.mirror : null;
      if (!m || !(key in m.values) || isBlankAnswer(m.values[key])) return false;
      return sameAnswer(answers[key], m.values[key]);
    },
    [prediction, answers]
  );

  // Only table cells: a cleared select (the middle burner's side offset) is an ordinary
  // unanswered question, which is required already.
  const mirrorRequiredCells = useMemo(
    () => (prediction?.on && prediction.mirror ? prediction.mirror.needsInput.filter((k) => k.includes("/")) : []),
    [prediction]
  );

  const needsMirrorInput = useCallback(
    (key: string) => !!prediction?.on && !!prediction.mirror && prediction.mirror.needsInput.includes(key) && isBlankAnswer(answers[key]),
    [prediction, answers]
  );

  // Run the whole pipeline over a state that was SET (a legacy prediction), the way the Item
  // Template load does: run-once startup/clear ops marked done so they cannot wipe it, the edge
  // guards synced so the set values are not read as the user switching housing / order type /
  // neck, then every operation, so derived values and defaults complete the configuration.
  const runAllOver = useCallback(
    (a: AnswerState) => {
      markRunOnceOpsComplete();
      syncEdgeGuards(a);
      const res = runOperations(
        operations,
        {
          answers: a,
          tables: tablesRef.current,
          selectedRows: selectedRowsRef.current,
          questions,
          qtables,
          tabs,
          materials,
          scratchpad: scratchpadRef.current,
          product,
        },
        {}
      );
      scratchpadRef.current = res.state.scratchpad;
      const sel = reconcileSelectedRows(res.state.selectedRows, res.state.answers);
      setAnswers(res.state.answers);
      setTables(res.state.tables);
      setSelectedRows(sel);
      return { answers: res.state.answers as AnswerState, tables: res.state.tables as TableState, selectedRows: sel };
    },
    [markRunOnceOpsComplete, syncEdgeGuards, operations, questions, qtables, tabs, materials, product]
  );

  // The operations over a state WITHOUT writing anything to the form: what a prediction made in
  // the background needs (PREDICTION_DEFAULT_ON false). Run-once operations count as already run,
  // as runAllOver marks them, and the scratchpad is a copy so the form's own stays untouched.
  const pureRun = useCallback(
    (a: AnswerState) => {
      const sp = { ...scratchpadRef.current } as Record<string, unknown>;
      const ran = new Set<string>(scratchpadRef.current._ranOnce instanceof Set ? (scratchpadRef.current._ranOnce as Set<string>) : []);
      for (const op of operations) if (spentWhenMarkedDone(op)) ran.add(op.id);
      sp._ranOnce = ran;
      const res = runOperations(
        operations,
        { answers: a, tables: tablesRef.current, selectedRows: selectedRowsRef.current, questions, qtables, tabs, materials, scratchpad: sp as Scratchpad, product },
        {}
      );
      const sel = reconcileSelectedRows(res.state.selectedRows, res.state.answers);
      return { answers: res.state.answers as AnswerState, tables: res.state.tables as TableState, selectedRows: sel };
    },
    [operations, questions, qtables, tabs, materials, product]
  );

  const applyPrediction = useCallback(
    async (input: PredictionInput, source: PredictionSource, mirrorPlan?: MirrorPlan | null, opts?: { replace?: boolean; held?: boolean; only?: (key: string) => boolean; provenance?: Record<string, { title: string; unsure: boolean }>; show?: boolean }) => {
      // A Recent pick replaces the prediction whole: start again from the form as it was before
      // any prediction, so nothing of the previous one (answers, housing row, tables) leaks into
      // it, and switching the prediction off still returns to that form. The refs are set here
      // because the code below reads them before the next render would.
      const prev = opts?.replace ? predictionRef.current : null;
      if (prev) {
        answersRef.current = prev.base;
        selectedRowsRef.current = prev.baseSelected;
        tablesRef.current = prev.baseTables;
        extraAuxRef.current = prev.baseExtraAux;
        setAnswers(prev.base);
        setSelectedRows(prev.baseSelected);
        setTables(prev.baseTables);
        setExtraAuxColumnsState(prev.baseExtraAux);
      }
      const held = !!opts?.held;
      // SHOWN, or kept in the background (PREDICTION_DEFAULT_ON)? The rep's own choices -- a
      // Recent pick, "Configure the opposite side" -- are always shown.
      const on = !!opts?.replace || !!opts?.held || !!opts?.show || PREDICTION_DEFAULT_ON;
      const base = { ...answersRef.current };
      const baseSelected = { ...selectedRowsRef.current };
      const baseTables = { ...tablesRef.current };
      const baseExtraAux = extraAuxRef.current;
      noteEvent(`prediction:${source.kind}:${source.basis}`);
      // A new configuration stays new: no edit baseline, no edit item.
      editBaselineRef.current = null;
      delete scratchpadRef.current.originKeep;
      if (opts?.only) {
        // THE FIRST PAGE ONLY (util/prediction launchKeys): those answers on the fresh form, then
        // every operation, the way a legacy prediction is completed. No housing is selected and
        // nothing is generated, so the operations leave the housing to the user. Whatever the
        // prediction changed is the prediction (grey, and what switching it off undoes).
        const only = opts.only;
        let source_: AnswerState;
        if ("configSnapshot" in input) {
          const snap = await parseConfigSnapshot(input.configSnapshot);
          if (snap.product !== product) return false;
          source_ = input.answers ?? snap.answers;
        } else {
          source_ = input.answers;
        }
        const firstPage: AnswerState = {};
        for (const [k, v] of Object.entries(normalizeLoaded(source_))) if (only(k)) firstPage[k] = v;
        const res = on ? runAllOver(normalizeLoaded({ ...base, ...firstPage })) : pureRun(normalizeLoaded({ ...base, ...firstPage }));
        // The first-page answers applied (even one equal to the blank form's -- it is still the
        // prediction, not something the user set) and whatever the operations changed with them.
        const predicted: AnswerState = {};
        for (const k of new Set([...Object.keys(res.answers), ...Object.keys(base)])) {
          if (k in firstPage || !sameAnswer(res.answers[k], base[k])) predicted[k] = res.answers[k] ?? null;
        }
        userEditedRef.current = new Set();
        setPrediction({
          on, predicted, base, baseSelected, baseTables, baseExtraAux,
          predSelected: res.selectedRows, predTables: res.tables, predExtraAux: baseExtraAux,
          launchSource: source, refineSource: null, mirror: null, held, provenance: opts.provenance ?? {},
        });
        return true;
      }
      if ("configSnapshot" in input) {
        const snap = await parseConfigSnapshot(input.configSnapshot);
        // A configuration from another configurator would be nonsense here.
        if (snap.product !== product) return false;
        // A mirrored MB burner: the sibling's housing selection and tables, the mirrored answers.
        const merged = normalizeLoaded(input.answers ?? snap.answers);
        const values: AnswerState = {};
        if (mirrorPlan) for (const k of mirrorPlan.changed) values[k] = merged[k] ?? null;
        setPrediction({
          on, predicted: merged, base, baseSelected, baseTables, baseExtraAux,
          predSelected: snap.selectedRows, predTables: snap.tables, predExtraAux: snap.extraAux,
          launchSource: source, refineSource: null,
          mirror: mirrorPlan ? { role: mirrorPlan.role, values, needsInput: mirrorPlan.needsInput } : null,
          held, provenance: {},
        });
        if (on) applySnapshotState(merged, snap);
        return true;
      }
      // Legacy: overlay the translated answers on the fresh form and let the operations
      // complete them. Everything that results is the prediction, so all of it shows grey.
      const res = on ? runAllOver(normalizeLoaded({ ...base, ...input.answers })) : pureRun(normalizeLoaded({ ...base, ...input.answers }));
      setPrediction({
        on, predicted: res.answers, base, baseSelected, baseTables, baseExtraAux,
        predSelected: res.selectedRows, predTables: res.tables, predExtraAux: baseExtraAux,
        launchSource: source, refineSource: null, mirror: null, held, provenance: {},
      });
      return true;
    },
    [product, normalizeLoaded, applySnapshotState, runAllOver, pureRun, setPrediction]
  );

  const refinePrediction = useCallback(
    async (input: PredictionInput, source: PredictionSource, leaveAlone: (key: string) => boolean, opts?: { userEdits?: boolean; provenance?: Record<string, { title: string; unsure: boolean }> }) => {
      const p = predictionRef.current;
      if (!p) return false;
      const cur = answersRef.current;
      if (!p.on) {
        // IN THE BACKGROUND (PREDICTION_DEFAULT_ON false, or switched off): the prediction is
        // kept up to date -- every page, as the form moves -- and nothing is written to it. The
        // switch fills it in on demand (setPredictionOn), and Save & Submit compares it with
        // what the rep configured. Every key is recorded, edited or not: the comparison needs
        // the prediction, and the switch already leaves an edited answer alone.
        let incoming: AnswerState;
        if ("configSnapshot" in input) {
          const snap = await parseConfigSnapshot(input.configSnapshot);
          if (snap.product !== product) return false;
          incoming = normalizeLoaded(input.answers ?? snap.answers);
        } else if (input.verbatim) {
          incoming = normalizeLoaded(input.answers);
        } else {
          incoming = pureRun(normalizeLoaded({ ...cur, ...input.answers })).answers;
        }
        const predicted: AnswerState = { ...p.predicted };
        const provenance = { ...p.provenance };
        for (const [k, v] of Object.entries(incoming)) {
          if (leaveAlone(k)) continue;
          predicted[k] = v;
          if (opts?.provenance?.[k]) provenance[k] = opts.provenance[k];
        }
        setPrediction({ ...p, predicted, refineSource: source, mirror: null, provenance });
        noteEvent(`prediction-refine:${source.kind}:${source.basis}:background`);
        return true;
      }
      // Only answers nobody has touched: still the launch prediction, or never set at all --
      // or, for the pages the launch left empty, never edited by the user.
      const untouched = opts?.userEdits
        ? (k: string) => !userEditedRef.current.has(k)
        : (k: string) => (k in p.predicted ? sameAnswer(cur[k], p.predicted[k]) : sameAnswer(cur[k], p.base[k]));
      const next: AnswerState = { ...cur };
      const predicted: AnswerState = { ...p.predicted };
      if ("configSnapshot" in input) {
        const snap = await parseConfigSnapshot(input.configSnapshot);
        if (snap.product !== product) return false;
        const incoming = normalizeLoaded(snap.answers);
        // A snapshot is a whole configuration: a key it does not hold is empty in it.
        for (const k of new Set([...Object.keys(incoming), ...Object.keys(p.predicted)])) {
          if (leaveAlone(k) || !untouched(k)) continue;
          const v = k in incoming ? incoming[k] : null;
          next[k] = v;
          predicted[k] = v;
        }
        setAnswers(next);
        // The extra Aux columns belong to the inlet tables, which are off the first page.
        if (extraAuxRef.current === p.predExtraAux) setExtraAuxColumnsState(snap.extraAux);
        markRunOnceOpsComplete();
        syncEdgeGuards(next);
        refreshDerived(next, tablesRef.current, selectedRowsRef.current);
        setPrediction({ ...p, predicted, predExtraAux: snap.extraAux, refineSource: source, mirror: null });
      } else if (input.verbatim) {
        // Field by field (src/util/fieldwise.ts): the chosen answers set as they are, the way a
        // snapshot is -- running the operations over them undid some (measured: fewer right).
        const incoming = normalizeLoaded(input.answers);
        for (const [k, v] of Object.entries(incoming)) {
          if (leaveAlone(k) || !untouched(k)) continue;
          next[k] = v;
          predicted[k] = v;
        }
        setAnswers(next);
        markRunOnceOpsComplete();
        syncEdgeGuards(next);
        refreshDerived(next, tablesRef.current, selectedRowsRef.current);
        const provenance = { ...p.provenance };
        for (const k of Object.keys(incoming)) if (k in predicted && opts?.provenance?.[k]) provenance[k] = opts.provenance[k];
        setPrediction({ ...p, predicted, refineSource: source, mirror: null, provenance });
      } else {
        // Legacy answers are a partial picture: only the keys they hold are replaced, then the
        // operations complete the rest.
        const incoming = normalizeLoaded(input.answers);
        for (const k of Object.keys(incoming)) {
          if (leaveAlone(k) || !untouched(k)) continue;
          next[k] = incoming[k];
        }
        const res = runAllOver(next);
        // What the refresh and the operations left on untouched answers is the prediction now.
        for (const k of Object.keys(res.answers)) {
          if (leaveAlone(k)) continue;
          if (k in p.predicted ? sameAnswer(cur[k], p.predicted[k]) : sameAnswer(cur[k], p.base[k])) predicted[k] = res.answers[k];
        }
        // Where an answer came from, for the ones this refresh set (the customer's PO landing
        // after the launch, fieldwiseRun withPo).
        const provenance = { ...p.provenance };
        if (opts?.provenance) for (const k of Object.keys(incoming)) if (k in predicted && opts.provenance[k]) provenance[k] = opts.provenance[k];
        setPrediction({ ...p, predicted, refineSource: source, mirror: null, provenance });
      }
      noteEvent(`prediction-refine:${source.kind}:${source.basis}`);
      return true;
    },
    [product, normalizeLoaded, syncEdgeGuards, refreshDerived, runAllOver, pureRun, setPrediction, markRunOnceOpsComplete]
  );

  const formCompleter = useCallback(() => {
    // Captured on FIRST USE, not when handed out: PredictLaunch asks for this in its mount
    // effect, before the provider's own mount run has set the startup defaults (mounting,
    // nipple, pilot nozzle, the inlet panels...). Captured then, every completed order lacked
    // those ~48 answers and lost its sections to a full cpq2 snapshot (Kevin's live test,
    // 2026-10-18). By the first completion the evidence has arrived and the form is settled.
    let fresh: { answers: AnswerState; tables: TableState; scratch: Record<string, unknown> } | null = null;
    return (a: AnswerState): AnswerState => {
      fresh ??= { answers: { ...answersRef.current }, tables: tablesRef.current, scratch: scratchpadRef.current as Record<string, unknown> };
      const merged: AnswerState = { ...fresh.answers, ...a };
      // As a load does: run-once startup/clear ops count as done, the edge guards read the set
      // values as already there (applySnapshotState / runAllOver).
      const sp: Record<string, unknown> = { ...fresh.scratch };
      const ran = new Set<string>(fresh.scratch._ranOnce instanceof Set ? (fresh.scratch._ranOnce as Set<string>) : []);
      for (const op of operations) if (spentWhenMarkedDone(op)) ran.add(op.id);
      sp._ranOnce = ran;
      const housing = String(merged["HOUSING/HOUSING"] ?? "");
      sp.prevHousing = housing;
      sp.neckPrevHousing = housing;
      sp.prevOrderTypeForHousingReset = orderTypeCodeOf(merged);
      sp.prevHasNeckForFlangeOd = hasNeckOf(merged);
      try {
        const res = runOperations(
          operations,
          { answers: merged, tables: JSON.parse(JSON.stringify(fresh.tables)), selectedRows: {}, questions, qtables, tabs, materials, scratchpad: sp as never, product },
          {}
        );
        return res.state.answers as AnswerState;
      } catch {
        return merged;
      }
    };
  }, [operations, questions, qtables, tabs, materials, product]);

  const setPredictionOn = useCallback(
    (on: boolean) => {
      const p = predictionRef.current;
      if (!p || p.on === on) return;
      const cur = answersRef.current;
      let next: AnswerState = { ...cur };
      let sel = selectedRowsRef.current;
      let tbl = tablesRef.current;
      let aux = extraAuxRef.current;
      const qOf = (k: string) => k.slice(0, k.indexOf("/"));
      // ON REPLACES, OFF RESTORES (Kevin, 2026-10-22): "if I type something then turn predictor
      // on the field answers should be replaced. but if i turn it off my previous answers should
      // come back." Switching on keeps what the form held (`restore`) and puts the prediction
      // over it, typed answers included; switching off puts that back -- except where the user
      // changed something while it was on, which is their latest answer and stays.
      let restore: PredictionState["restore"] = p.restore;
      if (on) {
        restore = { answers: { ...cur }, selected: sel, tables: tbl, extraAux: aux, changed: [] };
        editedSinceOnRef.current = new Set();
        // A question the prediction answers (an option chosen, or a non-blank value). One it
        // leaves blank keeps the user's answer: there is nothing to replace it with.
        const answeredQ = new Set<string>();
        for (const [k, v] of Object.entries(p.predicted)) if (!isBlankAnswer(v)) answeredQ.add(qOf(k));
        for (const [k, v] of Object.entries(p.predicted)) if (answeredQ.has(qOf(k))) next[k] = v;
        // A select is one answer over several keys: an option the user picked that the
        // prediction does not mention would otherwise stay on beside the predicted one.
        for (const k of Object.keys(next)) {
          if (next[k] === true && !(k in p.predicted) && answeredQ.has(qOf(k)) && Object.keys(p.predicted).some((pk) => qOf(pk) === qOf(k) && p.predicted[pk] === true)) next[k] = null;
        }
        restore.changed = Object.keys(next).filter((k) => !sameAnswer(next[k], cur[k]));
        // The housing row and its generated table travel together.
        if (Object.keys(p.predSelected).length) {
          sel = p.predSelected;
          tbl = { ...tbl, ...p.predTables };
        }
        aux = p.predExtraAux;
      } else if (restore) {
        // Question by question: one the user touched while it was on keeps its current answer.
        const editedQ = new Set([...editedSinceOnRef.current].map(qOf));
        for (const k of new Set([...restore.changed, ...Object.keys(p.predicted)])) {
          if (editedQ.has(qOf(k))) continue;
          if (k in restore.answers) next[k] = restore.answers[k];
          else delete next[k];
        }
        // A housing row picked while it was on is the user's pick and stays (with its table).
        const pickedRow = [...editedSinceOnRef.current].some((k) => /\/SELECT_HOUSING_\d+$/.test(k));
        if (!pickedRow) {
          sel = restore.selected;
          tbl = restore.tables;
        }
        aux = restore.extraAux;
      } else {
        // On since launch (PREDICTION_DEFAULT_ON), so there is no "before" but the blank form:
        // take back what is still the prediction, and leave what the user set -- even where it
        // equals the prediction (picking the predicted housing row is still the user's pick).
        const mine = userEditedRef.current;
        for (const k of Object.keys(p.predicted)) {
          if (!mine.has(k) && sameAnswer(cur[k], p.predicted[k])) {
            if (k in p.base) next[k] = p.base[k];
            else delete next[k];
          }
        }
        const pickedRow = [...mine].some((k) => /\/SELECT_HOUSING_\d+$/.test(k));
        if (!pickedRow && sameSelection(sel, p.predSelected)) {
          sel = p.baseSelected;
          tbl = { ...p.baseTables };
        }
        if (aux === p.predExtraAux) aux = p.baseExtraAux;
      }
      // Switched OFF: the defaults the predicted values stood in for come back. Switching on
      // spent the run-once ops (below) before there was a housing to default from; taking the
      // predicted values out left Hood Size, Neck Height and the face plate blank with nothing
      // able to fill them. Re-arm the ones whose questions are all blank again and run the
      // pipeline once -- what the user's next edit would run anyway.
      if (!on) {
        // The housing's Cell OD (and Cell OD 2/3, base cell size) are written only when the
        // selection CHANGES (operation "reset selection 0" compares against SELECTION). Taking
        // a predicted Cell OD out leaves the row selected with nothing to write it again, so
        // the pass reads the selected row as newly picked -- as clicking it again would.
        if ("SELECTION/SELECTION" in next) next["SELECTION/SELECTION"] = 0;
        syncEdgeGuards(next);
        rearmBlankRunOnceOps(operations, scratchpadRef.current as Record<string, unknown>, next);
        // The housing-change defaults are edge-guarded instead (neck + manway, ops 363/364;
        // hinge, op 394), and syncEdgeGuards just told them the housing has not changed. Where
        // everything such an op sets is blank again, let it fire as for a newly picked housing.
        // Never the guards whose ops CLEAR things (the order-type and neck-transition guards).
        const answeredQ = new Set(
          Object.entries(next)
            .filter(([, v]) => v === true || typeof v === "number" || (typeof v === "string" && v.trim() !== ""))
            .map(([k]) => k.slice(0, k.indexOf("/")))
        );
        const allBlank = (qs: string[]) => qs.every((q) => !answeredQ.has(q));
        const spg = scratchpadRef.current as Record<string, unknown>;
        if (allBlank(["NECK_HEIGHT", "MANWAYID", "MANWAY_DEPTH"])) spg.neckPrevHousing = "";
        if (allBlank(["HINGE_DIRECTION", "MOUNTING", "HINGE_SIZE"])) spg.prevHousing = "";
        try {
          const res = runOperations(
            operations,
            { answers: next, tables: tbl, selectedRows: sel, questions, qtables, tabs, materials, scratchpad: scratchpadRef.current, product },
            {}
          );
          scratchpadRef.current = res.state.scratchpad;
          next = res.state.answers as AnswerState;
          tbl = res.state.tables as TableState;
          sel = reconcileSelectedRows(res.state.selectedRows, res.state.answers);
        } catch (e) {
          console.warn("[cpq2] could not re-run the operations after switching the prediction off", e);
        }
      }
      setAnswers(next);
      setSelectedRows(sel);
      setTables(tbl);
      if (aux !== extraAuxRef.current) setExtraAuxColumnsState(aux);
      // As a load does (applySnapshotState, runAllOver): the run-once startup/clear operations
      // count as done for what was just set. The background prediction ran them over a COPY of
      // the scratchpad, so the form's own never saw Clear Qtable fire for the predicted BYOB,
      // and the rep's first edit ran it: the hand-typed housing row vanished (Kevin, Profire
      // SO 427443, 2026-10-19: "i clicked o-type and it set then blanked, i then clicked otype
      // again and it worked").
      if (on) markRunOnceOpsComplete();
      syncEdgeGuards(next);
      refreshDerived(next, tbl, sel);
      setPrediction({ ...p, on, restore });
      noteEvent(on ? "prediction-on" : "prediction-off");
    },
    [syncEdgeGuards, refreshDerived, setPrediction, markRunOnceOpsComplete, operations, questions, qtables, tabs, materials, product]
  );

  const predictionOutcome = useCallback((isScored: (key: string) => boolean) => {
    const p = predictionRef.current;
    if (!p) return null;
    const cur = answersRef.current;
    // WHAT WAS PREDICTED vs WHAT WAS CONFIGURED, over the answers the prediction actually gave
    // (a non-blank value, or a chosen option). A select is one answer: its predicted option
    // against its final option. Answers the prediction left blank are not counted -- in the
    // background the predicted map also carries the options a vote set to null, and the form's
    // own defaults (Flange Position centered) would otherwise read as "changes" nobody made.
    const text = (v: unknown) => (v === null || v === undefined || v === false ? "" : String(v));
    const question = (k: string) => k.slice(0, k.indexOf("/"));
    const option = (k: string) => k.slice(k.indexOf("/") + 1);
    const finalOption = new Map<string, string>();
    for (const [k, v] of Object.entries(cur)) if (v === true && isScored(k)) finalOption.set(question(k), option(k));
    const detail: Array<{ key: string; predicted: string; final: string; from?: string }> = [];
    const seen = new Set<string>();
    let predicted = 0;
    // Where an answer came from, in the hover's words without its hit rate ("this customer's
    // BT-SB38/38-24-4" / "what everyone orders" / "the first option offered"), so the outcome
    // log can score every source on live orders (2026-10-20).
    const fromOf = (k: string): string => {
      const t = (p.provenance[k] ?? p.provenance[question(k)])?.title ?? "";
      return t.replace(/^Predicted from /, "").replace(/; right \d+% of the time\.?.*$/, "").slice(0, 160);
    };
    const bySource: Record<string, number> = {};
    const count = (k: string) => { const f = fromOf(k) || "(no source recorded)"; bySource[f] = (bySource[f] ?? 0) + 1; };
    // Values the operations compute from the housing choice are not answers anyone compares.
    const computed = /^(HOUSING_VALIDATION|MODEL|SELECTION|CALCULATE2|FIRETUBE_DIAMETER1|CELL_QUANTITYV|HIDDEN_ID_KEY|DESCRIPTION|MAPPING)(\/|$)/;
    for (const [k, pv] of Object.entries(p.predicted)) {
      if (!isScored(k) || isBlankAnswer(pv) || computed.test(k)) continue;
      if (pv === true) {
        const q = question(k);
        if (seen.has(q)) continue;
        seen.add(q);
        predicted++;
        count(k);
        const fin = finalOption.get(q) ?? "";
        if (fin !== option(k)) detail.push({ key: q, predicted: option(k), final: fin, from: fromOf(k) });
      } else {
        predicted++;
        count(k);
        if (!sameAnswer(cur[k], pv)) detail.push({ key: k, predicted: text(pv), final: text(cur[k]), from: fromOf(k) });
      }
    }
    return { predicted, changed: detail.map((d) => d.key), detail, bySource };
  }, []);

  // Run startup defaults once on app load so questions like SIZE_TABLE/AUX_n
  // are seeded to "None" immediately (and their dependents like
  // MATCH_OFFSET_POSITION/AUX_n auto-lock via readonly rules) without
  // requiring the user to click Calculate first.
  const didMountRunRef = useRef(false);
  useEffect(() => {
    if (didMountRunRef.current) return;
    didMountRunRef.current = true;
    runOps();
    // We deliberately omit runOps from deps — we only want this once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AnswersCtx>(
    () => ({
      answers,
      tables,
      selectedRows,
      setAnswer,
      selectRow,
      runOps,
      invalidFields,
      setInvalidFields,
      customerPartNumber,
      setCustomerPartNumber,
      syncCustomerPartNumberForItem,
      applyCustomerPartNumber,
      extraAuxColumns,
      setExtraAuxColumns,
      loadItemTemplate,
      loadItemConfig,
      getEditBaseline,
      setEditBaseline,
      getConfigSnapshot,
      editItemId,
      configMode: editItemId ? "edit" : "new",
      configStatus,
      existingItem,
      existingItemFor,
      plannedName,
      forceNewConfig,
      editExistingItem,
      lockedItemId,
      noteLockedItem,
      adminOverride,
      setAdminOverride,
      mappingSpec,
      product,
      prediction: prediction
        ? {
            on: prediction.on,
            launchSource: prediction.launchSource,
            refineSource: prediction.refineSource,
            mirrorRole: prediction.mirror ? prediction.mirror.role : null,
            held: prediction.held,
          }
        : null,
      isPredicted,
      predictionNote,
      isMirrored,
      needsMirrorInput,
      mirrorRequiredCells,
      wasPredicted,
      applyPrediction,
      refinePrediction,
      setPredictionOn,
      predictionOutcome,
      formCompleter,
    }),
    [
      answers,
      tables,
      selectedRows,
      setAnswer,
      selectRow,
      runOps,
      invalidFields,
      setInvalidFields,
      customerPartNumber,
      syncCustomerPartNumberForItem,
      applyCustomerPartNumber,
      extraAuxColumns,
      setExtraAuxColumns,
      loadItemTemplate,
      loadItemConfig,
      getEditBaseline,
      setEditBaseline,
      getConfigSnapshot,
      editItemId,
      forceNewConfig,
      editExistingItem,
      lockedItemId,
      noteLockedItem,
      adminOverride,
      mappingSpec,
      product,
      configStatus,
      existingItem,
      existingItemFor,
      plannedName,
      prediction,
      isPredicted,
      predictionNote,
      isMirrored,
      needsMirrorInput,
      mirrorRequiredCells,
      wasPredicted,
      applyPrediction,
      refinePrediction,
      setPredictionOn,
      predictionOutcome,
      formCompleter,
    ]
  );

  // DIAGNOSIS FROM A LIVE PAGE. Kevin's page showed blanks a replay of the same launch did not
  // (2026-10-19), and nothing in the logs says what the page held. In the browser console:
  //   copy(cpq2PredictionReport())
  // then paste it. Read-only: the launch, the prediction's sources, every answer the user
  // edited, and for the answers that go blank (inlet cells, hood, face plate, neck) what the
  // page holds, what was predicted and where it came from.
  useEffect(() => {
    const w = window as unknown as { cpq2PredictionReport?: () => string };
    w.cpq2PredictionReport = () => {
      const p = predictionRef.current;
      const a = answersRef.current;
      const fw = currentFieldwise();
      const ctx = getLaunchContext();
      const watch = /^(UP|IN|MATCH_OFFSET_POSITION|SIZE_TABLE|ORIENTATION_TABLE|INLET_INSERT|FACE_PLATE_SIZE|FACE_PLATE_PEEPSIGHT|HOOD_TYPE|HOODSIZE|HOOD_SPACERS|NECK_HEIGHT|FLANGE_OD|FLANGE_GASKET|PATTERN_TYPE|BOLT_HOLE_QUANTITY|BOLT_HOLE_SIZE|BOLT_HOLE_CENTERS|FIRETUBE_DIAMETER|REQUIRED_HEAT_DUTY|HOUSING|DIMENSIONS|DESIGN|ORDER_TYPE|BURNER_QTY)(\/|$)/;
      const keys: Record<string, { current: unknown; predicted?: unknown; from?: string; edited: boolean }> = {};
      const all = new Set([...Object.keys(a), ...(p ? Object.keys(p.predicted) : [])]);
      for (const k of all) {
        if (!watch.test(k)) continue;
        const cur = a[k];
        const pr = p ? p.predicted[k] : undefined;
        if ((cur === null || cur === undefined || cur === false || cur === "") && (pr === null || pr === undefined || pr === false || pr === "")) continue;
        keys[k] = { current: cur, predicted: pr, from: p?.provenance?.[k]?.title, edited: userEditedRef.current.has(k) };
      }
      const build = Array.from(document.scripts).map((s) => s.src).find((s) => /assets\/index-/.test(s))?.replace(/.*\//, "");
      return JSON.stringify({
        at: new Date().toISOString(), build, product,
        launch: { customerId: ctx.customerId, transactionId: ctx.transactionId, originType: ctx.originType, user: ctx.userName, itemId: ctx.itemId },
        prediction: p ? { on: p.on, launch: p.launchSource, refine: p.refineSource ?? null, held: p.held ?? false, predicted: Object.keys(p.predicted).length, provenance: Object.keys(p.provenance ?? {}).length } : null,
        fieldwise: fw ? { sources: fw.sources.length, legacy: fw.sources.filter((s) => s.kind === "legacy").length, pick: fw.pick?.name ?? null, modelTrainedAt: fw.model.trainedAt, modelVoteKeys: Object.keys(fw.model.everyone.overall).length, ...describeFieldwise(fw) } : null,
        po: currentPoUse(),
        comparison: (() => { const o = predictionOutcome(scoredKeys(questions, tabs)); return o ? { predicted: o.predicted, changed: o.changed.length, detail: o.detail } : null; })(),
        userEdited: [...userEditedRef.current],
        keys,
      }, null, 1);
    };
    return () => { delete w.cpq2PredictionReport; };
  }, [product, predictionOutcome, questions, tabs]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAnswers(): AnswersCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAnswers must be used inside <AnswersProvider>");
  return ctx;
}
